import { Alert, Button, Grid, Group, Loader, Stack, Text } from '@mantine/core'
import { IconExclamationMark } from '@tabler/icons-react'

import { StripeProvider } from '@/components/StripeProvider'
import { OrderSummary } from '@/screens/OnboardingScreen/OrderSummary'
import type { Plan } from '@/services/api/api.types'

import { StripePaymentForm } from './StripePaymentForm'

interface PaymentFlowContentProps {
  // Payment flow state
  showPaymentForm: boolean
  clientSecret: string | null
  selectedPlanData: Plan | undefined
  plans: Plan[]
  autoRefill: boolean

  // Loading/error states
  isLoading: boolean
  hasError: boolean

  // Event handlers
  onPaymentSuccess: () => void
  onBackFromPayment: () => void
  onRetry: () => void

  // UI customization
  loadingText?: string
  mainText?: string
}

export function PaymentFlowContent({
  showPaymentForm,
  clientSecret,
  selectedPlanData,
  plans,
  autoRefill,
  isLoading,
  hasError,
  onPaymentSuccess,
  onBackFromPayment,
  onRetry,
  loadingText = 'Setting up your payment...',
  mainText = 'Enter your payment details',
}: PaymentFlowContentProps) {
  if (!showPaymentForm || !selectedPlanData) {
    return null
  }

  // Loading state while client secret is being generated
  if (!clientSecret && isLoading) {
    return (
      <Stack gap="xl" align="center" py="xl">
        <Text c="dimmed" ta="center" size="lg">
          {loadingText}
        </Text>
        <Loader size="lg" />
      </Stack>
    )
  }

  // Error state if client secret generation failed
  if (!clientSecret && hasError) {
    return (
      <Stack gap="xl">
        <Text c="dimmed" ta="center" size="lg">
          There was an error setting up your payment
        </Text>

        <Alert variant="light" color="red" title="Payment Setup Failed" icon={<IconExclamationMark />}>
          There was an error setting up your payment. Please try again or contact support if the problem persists.
        </Alert>

        <Group justify="space-between" mt="xl">
          <Button variant="default" onClick={onBackFromPayment} size="md">
            Back to Plan Selection
          </Button>
          <Button onClick={onRetry} loading={isLoading} size="md" color="indigo">
            Try Again
          </Button>
        </Group>
      </Stack>
    )
  }

  if (clientSecret) {
    return (
      <StripeProvider clientSecret={clientSecret}>
        <Stack gap="xl">
          <Text c="dimmed" ta="center" size="lg">
            {mainText}
          </Text>

          {autoRefill ? (
            // Auto-refill setup: Single column layout (no order summary)
            <StripePaymentForm
              onPaymentSuccess={onPaymentSuccess}
              onBack={onBackFromPayment}
              isUpdating={isLoading}
              clientSecret={clientSecret}
            />
          ) : (
            // Credit purchase: Two-column layout with order summary
            <Grid gutter="xl">
              {/* Left Column - Order Summary */}
              <Grid.Col span={{ base: 12, md: 5 }}>
                <OrderSummary selectedPlan={selectedPlanData} autoRefill={autoRefill} allPlans={plans} />
              </Grid.Col>

              {/* Right Column - Stripe Payment Form */}
              <Grid.Col span={{ base: 12, md: 7 }}>
                <StripePaymentForm
                  onPaymentSuccess={onPaymentSuccess}
                  onBack={onBackFromPayment}
                  isUpdating={isLoading}
                  clientSecret={clientSecret}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </StripeProvider>
    )
  }

  return null
}
